
import Vue, { PropType } from 'vue';
import { BvTableFieldArray } from 'bootstrap-vue';
import RuleTable from '@/components/common/RuleTable.vue';
import { DeviceCalibrationStatuses } from '@/interfaces/calibration';
import { required } from 'vuelidate/lib/validators';
import dayjs from '@/plugins/dayjs';
import {
	formDatePickerTranslations,
	formTimePickerTranslations,
} from '@/i18n/translations/vueBootstrap';

/*
	This section is picked up by vue-i18n. DO NOT REMOVE.

	// Status messages
	this.$t('Not_started_tooltip');
	this.$t('In_progress_tooltip');
	this.$t('Finished_tooltip');
	this.$t('Canceled_tooltip');
	this.$t('Timeout_tooltip');
*/

export default Vue.extend({
	components: {
		RuleTable,
	},

	data() {
		return {
			perPageOptions: [
				{ value: 15, text: 15 },
				{ value: 30, text: 30 },
				{ value: 50, text: 50 },
			],
			filter: null,
			totalRows: 1,
			currentPage: 1,
			perPage: 15,
			calibrationForm: {
				startDate: '',
				startTime: '',
				daylightMode: false,
				lightLevel: 50,
			},
		};
	},

	validations: {
		calibrationForm: {
			startDate: { required },
			startTime: { required },
			daylightMode: {},
			lightLevel: {},
		},
	},

	props: {
		name: String,
		isBusy: { type: Boolean, default: false },
		fields: Array as PropType<BvTableFieldArray>,
		rows: Array,
		sortBy: String,
		fixed: { type: Boolean, default: false },
		sortDesc: { type: Boolean, default: false },
		showAddButton: Boolean,
		addButtonColor: { type: String, default: 'primary' },
		addButtonTitle: String,
		deleteButtonTitle: String,
		clickableRow: Boolean,
		responsive: { type: Boolean, default: true },
		userPermission: { type: Boolean, default: true },
		hasOffRule: Boolean,
		hasIdentifyRule: Boolean,
		hasCalibrationButtons: { type: Boolean, default: false },
		locale: { type: String, default: 'en-gb' },
	},

	mounted() {
		if (this.rows) {
			this.totalRows = this.rows.length;
		}
	},

	computed: {
		innerWidth(): number {
			return innerWidth;
		},
		minDate(): string {
			return dayjs().format('YYYY-MM-DD');
		},
		dateTranslations() {
			return formDatePickerTranslations[this.locale];
		},
		timeTranslations() {
			return formTimePickerTranslations[this.locale];
		},
	},

	watch: {
		rows(rows: []) {
			this.totalRows = rows.length;
		},
	},

	methods: {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		rowPicked(row: Record<string, unknown>, index: number, event: MouseEvent): void {
			if (this.clickableRow) {
				this.$emit('navigate', row);
			}
		},

		addData(): void {
			this.$emit('addNewData');
		},

		editData(row: Record<string, unknown>): void {
			this.$emit('editData', row);
		},

		deleteData(row: Record<string, unknown>, allowed = true): void {
			if (allowed) {
				if (row.calibrate) {
					this.$emit('cancelCalibrateData', row);
				}
				this.$emit('deleteData', row);
			}
		},

		disableStatus(disableStatus: number): string {
			return disableStatus ? 'important' : 'danger';
		},

		calibreStatus(calibreStatus: number): string {
			if (calibreStatus === 5) {
				return 'info';
			}
			if (calibreStatus >= 1000) {
				return 'warning'; // calibaration is ongoing, but status holds Windows processID
			}

			const statusList: string[] = ['info', 'warning', 'success-alt', 'important', 'danger'];
			return statusList[calibreStatus || 0];
		},

		calibreTooltip(calibreStatus: number): string {
			if (calibreStatus === 5) {
				return 'Offset_error_tooltip';
			}
			if (calibreStatus >= 1000) {
				return 'In_progress_tooltip'; // calibaration is ongoing, but status holds Windows processID
			}

			const statusList: string[] = [
				'Not_started_tooltip',
				'In_progress_tooltip',
				'Finished_tooltip',
				'Canceled_tooltip',
				'Timeout_tooltip',
			];
			return statusList[calibreStatus || 0];
		},

		zoneCalibrationStatusText(calibreStatus: DeviceCalibrationStatuses): string {
			if (['Scheduled', 'Started'].includes(calibreStatus)) {
				return this.$t('Cancel calibration').toString();
			}
			return this.$t('Start calibration').toString();
		},

		zoneCalibrationStatusVariant(calibreStatus: DeviceCalibrationStatuses): string {
			if (['Scheduled', 'Started'].includes(calibreStatus)) {
				return 'danger';
			}
			return 'primary';
		},

		calibrateData(row: Record<string, unknown>): void {
			this.$emit('calibrateData', row);
		},

		changeCheckbox(row: Record<string, unknown>): void {
			this.$emit('changeCheckbox', row);
		},

		changeOff(row: Record<string, unknown>): void {
			this.$emit('changeOff', row);
		},

		changeIdentify(row: Record<string, unknown>): void {
			this.$emit('changeIdentify', row);
		},

		onFiltered(filtered: []): void {
			this.totalRows = filtered.length;
			this.currentPage = 1;
		},

		calibrateZone(row: Record<string, unknown>): void {
			let selectedDate = `${this.calibrationForm.startDate} ${this.calibrationForm.startTime}`;
			if (this.calibrationForm.startDate && this.calibrationForm.startTime) {
				selectedDate = new Date(`${selectedDate}`).toISOString();
			}
			this.$emit(
				'changeZoneCalibration',
				row,
				selectedDate,
				this.calibrationForm.daylightMode,
				this.calibrationForm.lightLevel
			);
		},
	},
});
